import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ResponsiveDialog from '../common/ResponsiveDialog';
import pettyCashApi from '../../services/pettyCashApi';
import type { ConfirmationView, MyConfirmation } from '../../types/pettyCash';
import { formatAmount, formatDate, formatDateTime, imprestFigures, parseOptionalAmount, quarterLabel, signedAmount } from '../../utils/pettyCash';

/**
 * The holder's quarterly confirmation: his float and the claims not yet reimbursed (read-only), the advances and the
 * cash he holds (typed), the expected cash and the difference, and his acknowledgement. One per quarter.
 */

interface Props {
  open: boolean;
  companyId: number;
  currency: string;
  /** The difference allowed (the imprest tolerance of the setup), for the hint only. */
  tolerance: number;
  onClose: () => void;
  onDone: (view: ConfirmationView) => void;
}

const ReadOnly: React.FC<{ label: string; value: React.ReactNode; strong?: boolean }> = ({ label, value, strong }) => (
  <Grid item xs={6}>
    <Typography variant="caption" color="text.secondary" component="div">{label}</Typography>
    <Typography variant="body2" fontWeight={strong ? 600 : 400}>{value}</Typography>
  </Grid>
);

const shown = (n: number | null | undefined) => (n === null || n === undefined ? '—' : formatAmount(n));

const ConfirmationDialog: React.FC<Props> = ({ open, companyId, currency, tolerance, onClose, onDone }) => {
  const [mine, setMine] = useState<MyConfirmation | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [advancesText, setAdvancesText] = useState('');
  const [cashText, setCashText] = useState('');
  const [note, setNote] = useState('');
  const [acknowledged, setAcknowledged] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !companyId) return undefined;
    let cancelled = false;
    setMine(null);
    setLoadError(null);
    setAdvancesText('');
    setCashText('');
    setNote('');
    setAcknowledged(false);
    setError(null);
    setBusy(false);
    pettyCashApi.myConfirmation(companyId)
      .then((m) => { if (!cancelled) setMine(m); })
      .catch((e: any) => { if (!cancelled) setLoadError(e?.response?.data?.message || 'Your figures could not be loaded'); });
    return () => { cancelled = true; };
  }, [open, companyId]);

  const advances = parseOptionalAmount(advancesText);
  const cash = parseOptionalAmount(cashText);
  const figures = mine?.figures;
  const live = imprestFigures({
    floatAmount: figures?.floatAmount ?? null,
    outstanding: figures?.outstanding ?? 0,
    thisClaim: 0,
    advances: advances.value,
    cashInHand: cash.value,
    tolerance,
    mode: 'off',
  });
  const beyond = live.difference !== null && Math.abs(live.difference) > (Number(tolerance) || 0) + 1e-9;
  const due = mine?.due;
  const done = mine?.confirmation;

  const submit = async () => {
    if (!due) return;
    if (!cashText.trim() || !cash.ok || cash.value === null) { setError('Type the cash in hand (0 or more).'); return; }
    if (!advances.ok) { setError('The advances are not an amount.'); return; }
    if (!acknowledged) { setError('Tick the confirmation that the figures match the cash and receipts you hold.'); return; }
    setBusy(true);
    setError(null);
    try {
      const view = await pettyCashApi.confirm({
        companyId,
        quarter: due.quarter,
        advances: advances.value,
        cashInHand: cash.value,
        note: note.trim() || null,
        acknowledged: true,
      });
      onDone(view);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'The confirmation could not be saved');
    } finally {
      setBusy(false);
    }
  };

  return (
    <ResponsiveDialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth aria-labelledby="pc-confirm-title">
      <DialogTitle id="pc-confirm-title">Quarterly petty cash confirmation{due ? `: ${quarterLabel(due.quarter)}` : ''}</DialogTitle>
      <DialogContent dividers>
        {loadError ? <Alert severity="error">{loadError}</Alert> : !mine ? (
          <Stack direction="row" spacing={1} alignItems="center"><CircularProgress size={18} /><Typography variant="body2">Loading your figures…</Typography></Stack>
        ) : done ? (
          <Stack spacing={1}>
            <Alert severity="success">You confirmed {quarterLabel(done.quarter)} on {formatDateTime(done.confirmedAt)}.</Alert>
            <Typography variant="body2">
              Cash in hand {formatAmount(done.cashInHand)} {currency}
              {done.expectedCash !== null ? ` · expected ${formatAmount(done.expectedCash)}` : ''}
              {done.difference !== null ? ` · difference ${signedAmount(done.difference)}` : ''}
            </Typography>
            {done.reviewedAt ? <Typography variant="caption" color="text.secondary">Reviewed{done.reviewedByName ? ` by ${done.reviewedByName}` : ''} {formatDateTime(done.reviewedAt)}</Typography> : null}
          </Stack>
        ) : !due ? (
          <Typography variant="body2">No confirmation is due now.</Typography>
        ) : (
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Count the cash in your custody and confirm it with the figures below, by {formatDate(due.windowEnd)}. Finance reviews every holder&apos;s confirmation.
            </Typography>
            {/* Each spaced Grid sits in a Box: a Stack resets its children's margins, which ran the Grid past the edge. */}
            <Box>
              <Grid container spacing={1.5}>
                <ReadOnly label={`Float ${currency}`.trim()} value={shown(figures?.floatAmount)} />
                <ReadOnly label="Claims not yet reimbursed" value={shown(figures?.outstanding ?? 0)} />
              </Grid>
            </Box>
            <Box>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  id="pc-confirm-advances"
                  label="Advances and bills not yet claimed"
                  value={advancesText}
                  onChange={(e) => setAdvancesText(e.target.value.replace(/[^\d.,]/g, '').slice(0, 20))}
                  inputProps={{ inputMode: 'decimal' }}
                  error={!advances.ok}
                  helperText={!advances.ok ? 'Not an amount' : 'Cash handed out, or bills paid that are on no claim yet'}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  id="pc-confirm-cash"
                  label={`Cash in hand ${currency}`.trim()}
                  value={cashText}
                  onChange={(e) => setCashText(e.target.value.replace(/[^\d.,]/g, '').slice(0, 20))}
                  inputProps={{ inputMode: 'decimal' }}
                  error={!!cashText.trim() && !cash.ok}
                  helperText={!cash.ok ? 'Not an amount' : 'The cash you counted'}
                  required
                  fullWidth
                />
              </Grid>
            </Grid>
            </Box>
            <Box>
              <Grid container spacing={1.5}>
                <ReadOnly label="Expected cash in hand" value={shown(live.expectedCash)} strong />
                <ReadOnly label="Difference" value={live.difference === null ? '—' : signedAmount(live.difference)} strong />
              </Grid>
            </Box>
            {beyond ? <Alert severity="warning">The cash differs from the expected cash: explain it in the note.</Alert> : null}
            <TextField id="pc-confirm-note" label="Note" value={note} onChange={(e) => setNote(e.target.value.slice(0, 1000))} multiline minRows={2} fullWidth />
            <FormControlLabel
              control={<Checkbox checked={acknowledged} onChange={(e) => setAcknowledged(e.target.checked)} />}
              label="The cash and receipts in my custody match these figures"
            />
            {error ? <Alert severity="error">{error}</Alert> : null}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose} disabled={busy}>{due && !done ? 'Later' : 'Close'}</Button>
        {due && !done ? (
          <Button onClick={() => { void submit(); }} disabled={busy || !acknowledged}>{busy ? 'Saving…' : 'Confirm'}</Button>
        ) : null}
      </DialogActions>
    </ResponsiveDialog>
  );
};

export default ConfirmationDialog;
